/**
 * Gutenberg emits borders in two mutually exclusive shapes:
 *
 *   flat   -> { width, style, color, radius }              (all sides linked)
 *   split  -> { top: {...}, right: {...}, ..., radius }    (sides unlinked)
 *
 * Radius has the same split: a shorthand string when the corners are linked,
 * a per-corner object when they are not. Interpolating the object form
 * straight into CSS yields "[object Object]".
 *
 * A side may also be partial (color only, width only). CSS defaults
 * border-style to `none`, so a width/color without a style renders nothing —
 * fall back to `solid` whenever a side has any value but no explicit style.
 */

export type RadiusCorners = {
  topLeft?: string;
  topRight?: string;
  bottomLeft?: string;
  bottomRight?: string;
};

export type BorderRadius = string | RadiusCorners | null | undefined;

export type BorderSide = {
  width?: string;
  style?: string;
  color?: string;
};

export type Border =
  | (BorderSide & {
      radius?: BorderRadius;
      top?: BorderSide;
      right?: BorderSide;
      bottom?: BorderSide;
      left?: BorderSide;
    })
  | null
  | undefined;

const SIDES = ["top", "right", "bottom", "left"] as const;

/**
 * theme.json presets serialize as "var:preset|color|primary" — turn that into
 * the custom property the theme actually emits.
 */
export const toCssValue = (value?: string | null): string => {
  if (!value) return "";

  const trimmed = String(value).trim();

  if (trimmed.startsWith("var:")) {
    return `var(--wp--${trimmed.slice(4).replace(/\|/g, "--")})`;
  }

  return trimmed;
};

/**
 * Normalize a border radius to a CSS value.
 *
 * "12px"                                     -> "12px"
 * { topLeft: "12px", topRight: "12px",
 *   bottomRight: "8px", bottomLeft: "8px" }  -> "12px 12px 8px 8px"
 *
 * Unset corners fall back to 0. Returns "" when there is nothing to emit.
 */
export const getBorderRadius = (radius: BorderRadius): string => {
  if (!radius) return "";

  if (typeof radius === "string") return toCssValue(radius);

  if (typeof radius !== "object") return "";

  const { topLeft, topRight, bottomRight, bottomLeft } = radius;

  if (!topLeft && !topRight && !bottomRight && !bottomLeft) return "";

  // CSS corner order: top-left, top-right, bottom-right, bottom-left
  return [topLeft, topRight, bottomRight, bottomLeft]
    .map((corner) => toCssValue(corner) || "0")
    .join(" ");
};

/** Ready-to-interpolate radius declaration, "" when no radius is set. */
export const getBorderRadiusCss = (radius: BorderRadius): string => {
  const value = getBorderRadius(radius);

  return value ? `border-radius: ${value};` : "";
};

const sideDeclarations = (side: BorderSide | undefined, suffix: string): string[] => {
  if (!side) return [];

  const width = toCssValue(side.width);
  const color = toCssValue(side.color);
  const style = side.style || (width || color ? "solid" : "");

  if (!width && !color && !style) return [];

  const prop = suffix ? `border-${suffix}` : "border";

  return [
    width ? `${prop}-width: ${width};` : "",
    style ? `${prop}-style: ${style};` : "",
    color ? `${prop}-color: ${color};` : "",
  ].filter(Boolean);
};

/**
 * Build the full border CSS for a Gutenberg `style.border` object.
 * Returns "" when there is nothing to emit.
 *
 * getBorderCSS({ width: "12px", style: "dashed", color: "#e71515",
 *                radius: { topLeft: "12px", ... } })
 *   -> "border-width: 12px; border-style: dashed; border-color: #e71515;
 *       border-radius: 12px 12px 12px 12px;"
 */
export const getBorderCSS = (border: Border): string => {
  if (!border || typeof border !== "object") return "";

  const declarations: string[] = [];

  // Flat border — only when no per-side object is present, otherwise the
  // shorthand would fight the sides it was unlinked from.
  const hasSides = SIDES.some((side) => border[side]);

  if (hasSides) {
    SIDES.forEach((side) => {
      declarations.push(...sideDeclarations(border[side], side));
    });
  } else {
    declarations.push(...sideDeclarations(border, ""));
  }

  const radiusCss = getBorderRadiusCss(border.radius);

  if (radiusCss) declarations.push(radiusCss);

  return declarations.join(" ");
};
